// Audit Trail Service
// Logs all user actions for compliance and tracking

class AuditService {
  constructor() {
    this.logs = [];
    this.maxLogs = 10000; // Keep last 10,000 logs in memory
    this.initialized = false;
  }

  // Load logs from localStorage (lazy initialization)
  loadLogsFromStorage() {
    if (this.initialized) return;
    
    try {
      const stored = localStorage.getItem('auditLogs');
      if (stored) {
        this.logs = JSON.parse(stored);
        console.log(`📋 Loaded ${this.logs.length} audit logs from storage`);
      }
      this.initialized = true;
    } catch (error) {
      console.error('❌ Error loading audit logs:', error);
      this.logs = [];
      this.initialized = true;
    }
  }

  // Save logs to localStorage
  saveLogsToStorage() {
    try {
      localStorage.setItem('auditLogs', JSON.stringify(this.logs));
    } catch (error) {
      console.error('❌ Error saving audit logs:', error);
    }
  }

  // Get current user info
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        return {
          id: user.id || 'unknown',
          name: user.full_name || user.email || 'Unknown User',
          email: user.email || 'unknown@email.com'
        };
      }
    } catch (error) {
      console.error('❌ Error getting current user:', error);
    }
    
    return {
      id: 'system',
      name: 'System',
      email: 'system@app.com'
    };
  }

  // Log an action
  log(action, entityType, entityId, entityName, details = {}) {
    this.loadLogsFromStorage(); // Ensure initialized
    
    const user = this.getCurrentUser();
    const timestamp = new Date().toISOString();
    
    const logEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp,
      action, // 'create', 'update', 'delete', 'view'
      entityType, // 'organizations', 'locations', 'users', 'roles', etc.
      entityId,
      entityName,
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      details, // Additional context (e.g., changed fields)
      ipAddress: 'N/A', // Could be added if needed
      userAgent: navigator.userAgent
    };

    this.logs.unshift(logEntry); // Add to beginning

    // Keep only max logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Save to storage
    this.saveLogsToStorage();

    console.log('📋 Audit Log:', logEntry);

    return logEntry;
  }

  // Convenience methods for common actions
  logCreate(entityType, entityId, entityName, details = {}) {
    return this.log('create', entityType, entityId, entityName, details);
  }

  logUpdate(entityType, entityId, entityName, details = {}) {
    return this.log('update', entityType, entityId, entityName, details);
  }

  logDelete(entityType, entityId, entityName, details = {}) {
    return this.log('delete', entityType, entityId, entityName, details);
  }

  logView(entityType, entityId, entityName, details = {}) {
    return this.log('view', entityType, entityId, entityName, details);
  }

  // Get all logs
  getAllLogs() {
    this.loadLogsFromStorage(); // Ensure initialized
    return [...this.logs];
  }

  // Get logs filtered by criteria
  getFilteredLogs(filters = {}) {
    this.loadLogsFromStorage(); // Ensure initialized
    let filtered = [...this.logs];

    if (filters.action) {
      filtered = filtered.filter(log => log.action === filters.action);
    }

    if (filters.entityType) {
      filtered = filtered.filter(log => log.entityType === filters.entityType);
    }

    if (filters.userId) {
      filtered = filtered.filter(log => log.userId === filters.userId);
    }

    if (filters.startDate) {
      filtered = filtered.filter(log => new Date(log.timestamp) >= new Date(filters.startDate));
    }

    if (filters.endDate) {
      filtered = filtered.filter(log => new Date(log.timestamp) <= new Date(filters.endDate));
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        log.entityName?.toLowerCase().includes(query) ||
        log.userName?.toLowerCase().includes(query) ||
        log.userEmail?.toLowerCase().includes(query) ||
        log.action?.toLowerCase().includes(query) ||
        log.entityType?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }

  // Get logs for a specific entity
  getEntityLogs(entityType, entityId) {
    return this.logs.filter(log => 
      log.entityType === entityType && log.entityId === entityId
    );
  }

  // Get logs for a specific user
  getUserLogs(userId) {
    return this.logs.filter(log => log.userId === userId);
  }

  // Clear all logs (admin only)
  clearLogs() {
    this.logs = [];
    this.saveLogsToStorage();
    console.log('🗑️ All audit logs cleared');
  }

  // Export logs as JSON
  exportLogs() {
    return JSON.stringify(this.logs, null, 2);
  }

  // Get statistics
  getStatistics() {
    this.loadLogsFromStorage(); // Ensure initialized
    
    const stats = {
      totalLogs: this.logs.length,
      byAction: {},
      byEntityType: {},
      byUser: {},
      recentActivity: this.logs.slice(0, 10)
    };

    this.logs.forEach(log => {
      // Count by action
      stats.byAction[log.action] = (stats.byAction[log.action] || 0) + 1;
      
      // Count by entity type
      stats.byEntityType[log.entityType] = (stats.byEntityType[log.entityType] || 0) + 1;
      
      // Count by user
      stats.byUser[log.userName] = (stats.byUser[log.userName] || 0) + 1;
    });

    return stats;
  }
}

// Create singleton instance
const auditService = new AuditService();

export default auditService;

